/**
 * The progress screen's numbers — docs/33 C1.
 *
 * Describes, never grades: per medicine, what became of each of the last
 * seven days' doses; a month of days marked "all answered", "some" or
 * "none"; and plain counts. No percentage, no colour that means good or
 * bad, no comparison (docs/05 anti-goals; RX-ADH-03).
 */
import { addDaysToLocalDate, localDateAt } from './time.js';
import type { LocalDate } from './types.js';

export interface ProgressDose {
  medicationId: string;
  medicationName: string;
  dueAt: number;
  windowEnd: number;
  status: string;
}

/** What a day looks like: every dose answered, some, none, nothing due, or still to come. */
export type DayMark = 'all' | 'some' | 'none' | 'empty' | 'ahead';

export interface Progress {
  /** Oldest first. */
  week: LocalDate[];
  medicines: Array<{ medicationId: string; name: string; days: DayMark[] }>;
  /** Every day of the month `today` is in, first to last. */
  month: Array<{ date: LocalDate; mark: DayMark }>;
  counts: { taken: number; skipped: number; missed: number; due: number };
}

const ANSWERED = new Set(['taken', 'skipped', 'refused']);
const CLOSED_UNANSWERED = new Set(['missed', 'unreported']);

function markOf(statuses: string[], ahead: boolean): DayMark {
  if (statuses.length === 0) return ahead ? 'ahead' : 'empty';
  const answered = statuses.filter((s) => ANSWERED.has(s)).length;
  const settled = statuses.filter((s) => ANSWERED.has(s) || CLOSED_UNANSWERED.has(s)).length;
  if (settled < statuses.length && answered === settled) return 'ahead';
  if (answered === statuses.length) return 'all';
  return answered > 0 ? 'some' : 'none';
}

export function progress(doses: readonly ProgressDose[], today: LocalDate, timeZone: string, now: number): Progress {
  const settledStatus = (d: ProgressDose) =>
    d.status === 'pending' && d.windowEnd <= now ? 'missed' : d.status;
  const kept = doses.filter((d) => d.status !== 'moved');
  const byDay = new Map<LocalDate, ProgressDose[]>();
  for (const d of kept) {
    const day = localDateAt(d.dueAt, timeZone);
    byDay.set(day, [...(byDay.get(day) ?? []), d]);
  }
  const week = [-6, -5, -4, -3, -2, -1, 0].map((n) => addDaysToLocalDate(today, n));
  const names = new Map<string, string>();
  for (const d of kept) names.set(d.medicationId, d.medicationName);
  const medicines = [...names.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([medicationId, name]) => ({
      medicationId,
      name,
      days: week.map((day) => markOf((byDay.get(day) ?? []).filter((d) => d.medicationId === medicationId).map(settledStatus), day >= today)),
    }));
  const [y, m] = today.split('-').map(Number) as [number, number];
  const first = `${y}-${String(m).padStart(2, '0')}-01` as LocalDate;
  const month: Progress['month'] = [];
  for (let day = first; day.slice(0, 7) === first.slice(0, 7); day = addDaysToLocalDate(day, 1)) {
    month.push({ date: day, mark: markOf((byDay.get(day) ?? []).map(settledStatus), day >= today) });
  }
  const inMonth = kept.filter((d) => localDateAt(d.dueAt, timeZone).slice(0, 7) === first.slice(0, 7) && settledStatus(d) !== 'pending');
  const counts = {
    taken: inMonth.filter((d) => d.status === 'taken').length,
    skipped: inMonth.filter((d) => d.status === 'skipped' || d.status === 'refused').length,
    missed: inMonth.filter((d) => CLOSED_UNANSWERED.has(settledStatus(d))).length,
    due: inMonth.length,
  };
  return { week, medicines, month, counts };
}
